<template>
  <div
    class="cart-panel"
    :class="{ 'cart-panel--open': isOpen }"
    :aria-hidden="!isOpen"
    :inert="!isOpen"
    role="region"
    :aria-labelledby="cartTitleId"
  >
    <div class="cart-header">
      <h2 :id="cartTitleId">{{ t('cart.title') }}</h2>
      <button class="cart-close-btn" type="button" aria-label="Cerrar carrito" @click="closeCart">&times;</button>
    </div>
    
    <div v-if="cartItems.length === 0" class="cart-empty" role="status" aria-live="polite">
      <p>{{ t('cart.empty') }}</p>
    </div>
    
    <ul v-else class="cart-items">
      <li v-for="(item, index) in cartItems" :key="item.id" class="cart-item">
        <div class="cart-item-info">
          <h3>{{ item.name }}</h3>
          <p class="cart-item-price">{{ formatCurrency(item.price + item.extrasTotal) }}</p>
          <div v-if="item.extras && Array.isArray(item.extras) && item.extras.length > 0" class="cart-item-extras">
            <span v-for="extra in item.extras" :key="extra" class="cart-item-extra">
              + {{ extra }}
            </span>
          </div>
        </div>
        
        <div class="cart-item-controls">
          <button
            class="edit-btn"
            :aria-label="`Editar ${item.name}`"
            title="Editar opciones"
            @click="editItem(item, index)"
          >
            Editar
          </button>
          <div class="quantity-controls">
            <button 
              :aria-label="`Reducir cantidad de ${item.name}`"
              :disabled="item.quantity <= 1" 
              class="quantity-btn"
              @click="updateItemQuantity(index, -1)"
            >
              -
            </button>
            <span class="quantity-value" :aria-label="`Cantidad: ${item.quantity}`">{{ item.quantity }}</span>
            <button 
              :aria-label="`Aumentar cantidad de ${item.name}`"
              class="quantity-btn" 
              @click="updateItemQuantity(index, 1)"
            >
              +
            </button>
          </div>
          <button 
            class="remove-btn" 
            :aria-label="`Eliminar ${item.name}`"
            title="Eliminar producto"
            @click="removeItem(index)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </li>
    </ul>
    
    <div v-if="cartItems.length > 0" class="cart-footer">
      <div class="cart-total">
        <span>{{ t('cart.total') }}:</span>
        <strong>{{ formatCurrency(totalPrice) }}</strong>
      </div>
      <button 
        class="checkout-btn" 
        :disabled="totalItems === 0"
        @click="proceedToCheckout"
      >
        {{ t('cart.checkout') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCartStore } from '../../stores/cart-store';
import { formatCurrency } from '@shared/utils/currency';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'checkout'): void;
  (e: 'edit-item', item: any, index: number): void;
}>();

const cartStore = useCartStore();
const { t } = useI18n();
const cartTitleId = `cart-title-${useId()}`;

const cartItems = computed(() => cartStore.items);
const totalItems = computed(() => cartStore.totalItems);
const totalPrice = computed(() => cartStore.totalPrice);

const closeCart = () => {
  emit('close');
};

const editItem = (item: any, index: number) => {
  emit('edit-item', item, index);
};

const updateItemQuantity = (index: number, delta: number) => {
  cartStore.updateItemQuantity(index, delta);
};

const removeItem = (index: number) => {
  cartStore.removeItem(index);
};

const proceedToCheckout = () => {
  if (totalItems.value > 0) {
    emit('checkout');
  }
};

</script>

<style scoped>
.cart-panel {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 13100;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  pointer-events: none;
}

.cart-panel--open {
  transform: translateX(0);
  pointer-events: auto;
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
}

.cart-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.cart-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-empty {
  padding: 32px 16px;
  text-align: center;
  color: #666;
}

.cart-items {
  list-style: none;
  margin: 0;
  padding: 16px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  gap: 0.75rem;
}

.cart-item-info {
  flex: 1;
  min-width: 0;
}

.cart-item-info h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  color: #333;
}

.cart-item-price {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.cart-item-extras {
  margin-top: 4px;
}

.cart-item-extra {
  display: inline-block;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin-right: 4px;
  margin-top: 2px;
}

.cart-item-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.edit-btn {
  min-width: 44px;
  min-height: 44px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #dbe2ea;
  background: #f8fafc;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.edit-btn:hover {
  background: #eff6ff;
  border-color: #3b82f6;
}

.quantity-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.quantity-btn {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border: 1px solid #ccc;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-value {
  min-width: 24px;
  text-align: center;
  font-weight: 600;
}

.remove-btn {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 8px;
  border: 1px solid #fee2e2;
  background: #fef2f2;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.remove-btn:hover {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
  transform: scale(1.05);
}

.remove-btn:active {
  transform: scale(0.95);
}

.remove-btn:focus-visible {
  outline: 2px solid #ef4444;
  outline-offset: 2px;
}

.remove-btn svg {
  width: 16px;
  height: 16px;
}

.cart-footer {
  padding: 16px;
  border-top: 1px solid #eee;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
}

.cart-total {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 18px;
}

.checkout-btn {
  width: 100%;
  padding: 12px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.checkout-btn:hover:not(:disabled) {
  background: #2563eb;
}

.checkout-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}
</style>
